import { CalendarTaskSync } from '@griply/types';
import React, { useMemo } from 'react';
import { useTimeBasedStyle } from 'shared/components/ui/full-calendar-event/use-time-based-style';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { SmartList } from 'shared/types/smart-list';
import { Tag } from 'shared/types/tag';
import { Task, TaskCheckOptions } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import { TaskBasicEvent } from './task-basic-event';
import { TaskGoalEvent } from './task-goal-event';
import { TaskLifeAreaEvent } from './task-life-area-event';
import { TaskSmartListEvent } from './task-smart-list-event';
import { TaskTagEvent } from './task-tag-event';

export type TaskEventProps = {
  taskId: ID;
  weekStartsOn: WeekDays;
  occurrenceId?: ID;
  startDate?: string;
  endDate?: string;
  timeText?: string;
  completed?: boolean;
  checkOption?: TaskCheckOptions;
  isFutureRepeating?: boolean;
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  smartLists: SmartList[];
  onComplete: (id: ID, date: Timestamp) => void;
  onClick?: (id: ID) => void;
  isDeadline?: boolean;
  showDeadlineFlag?: boolean;
  onInteractionComplete?: () => void;
  // The dragged task from a side-panel drop carries its full task here. It lets
  // the drag mirror render when the task isn't in the calendar's windowed
  // `tasks` (e.g. a dateless or out-of-window task) — otherwise the preview is
  // invisible during the drag.
  fallbackTask?: Task;
  calendarSync?: CalendarTaskSync;
};

export const TaskEvent: React.FC<TaskEventProps> = ({
  taskId,
  weekStartsOn,
  occurrenceId,
  startDate,
  endDate,
  timeText,
  completed,
  checkOption,
  isFutureRepeating,
  tasks,
  goals,
  lifeAreas,
  tags,
  smartLists,
  onComplete,
  onClick,
  isDeadline,
  showDeadlineFlag,
  onInteractionComplete,
  fallbackTask,
  calendarSync,
}) => {
  const task = useMemo(
    () => tasks.find(({ id }) => id === taskId) ?? fallbackTask,
    [taskId, tasks, fallbackTask],
  );

  const { numberOfLines } = useTimeBasedStyle(startDate, endDate);

  const color = useMemo(() => {
    const linkedId = task?.goalId ?? task?.lifeAreaId;
    if (!linkedId) {
      return undefined;
    }

    return getLinkedItemColor(linkedId, { goals, lifeAreas });
  }, [goals, lifeAreas, task?.goalId, task?.lifeAreaId]);

  if (!task || !startDate) {
    return null;
  }

  if (task.action?.type === TaskActionType.GoalTimeslot) {
    return (
      <TaskGoalEvent
        task={task}
        goals={goals}
        lifeAreas={lifeAreas}
        onClick={onClick}
        numberOfLines={numberOfLines}
        timeText={timeText}
        completed={completed}
        onInteractionComplete={onInteractionComplete}
        calendarSync={calendarSync}
      />
    );
  }

  if (task.action?.type === TaskActionType.LifeAreaTimeslot) {
    return (
      <TaskLifeAreaEvent
        task={task}
        lifeAreas={lifeAreas}
        onClick={onClick}
        numberOfLines={numberOfLines}
        timeText={timeText}
        completed={completed}
        onInteractionComplete={onInteractionComplete}
        calendarSync={calendarSync}
      />
    );
  }

  if (task.action?.type === TaskActionType.TagTimeslot) {
    return (
      <TaskTagEvent
        task={task}
        tags={tags}
        onClick={onClick}
        numberOfLines={numberOfLines}
        timeText={timeText}
        completed={completed}
        onInteractionComplete={onInteractionComplete}
        calendarSync={calendarSync}
      />
    );
  }

  if (task.action?.type === TaskActionType.SmartListTimeslot) {
    return (
      <TaskSmartListEvent
        task={task}
        smartLists={smartLists}
        onClick={onClick}
        numberOfLines={numberOfLines}
        timeText={timeText}
        completed={completed}
        onInteractionComplete={onInteractionComplete}
        calendarSync={calendarSync}
      />
    );
  }

  return (
    <TaskBasicEvent
      task={task}
      weekStartsOn={weekStartsOn}
      occurrenceId={occurrenceId}
      color={color ?? undefined}
      referenceDate={startDate}
      checkOption={checkOption}
      isFutureRepeating={isFutureRepeating}
      onComplete={onComplete}
      isDeadline={isDeadline}
      showDeadlineFlag={showDeadlineFlag}
      onClick={onClick}
      numberOfLines={numberOfLines}
      timeText={timeText}
      completed={completed}
      onInteractionComplete={onInteractionComplete}
      calendarSync={calendarSync}
    />
  );
};
